import { useMemo, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { Rocket } from 'lucide-react'
import type { Schemas } from '@npc/api-client'

import { ApiFailure } from '../../../app/errors'
import { duration, hhmm, monthDayTime, bytes } from '../../../app/format'
import { eventsForProject, reduceLiveEvents } from '../../../app/live-activity'
import { useLiveActivity } from '../../../app/live-events'
import {
  isExecutingDevelopmentTask,
  developmentTaskNeedsDecision,
  visibleTaskElapsedMs,
} from '../../../app/development-tasks'
import {
  useProject,
  useProjectDevelopmentTasks,
  useProjectEnvironments,
  useProjectImportByProject,
  useProjectTimeline,
  useProjectStaticSiteSummary,
  useProjectResources,
} from '../../../app/queries'
import { T } from '../../../copy/text'
import { PJ } from '../../../copy/pjshell-text'
import { ModuleEvolutionSection } from '../module-evolution'
import { ProjectImportCard } from '../import-card'
import { diskCapacityId, diskCapacityKind, receiptOpenHref } from './resources'
import { environmentOf, environmentStatusCopy } from './environments'
import { useProjectId } from './params'
import {
  GroupLabel,
  KvRows,
  ModuleError,
  ModuleHeader,
  ModuleNotice,
  ModulePage,
  ModulePanel,
  ModuleSkeleton,
  Unverified,
  Uncollected,
  luluButtonClass,
  luluPrimaryButtonClass,
} from './ui'

export function ProjectOverviewPage() {
  const projectId = useProjectId()
  if (!projectId) {
    return (
      <ModulePage testid="project-module-overview">
        <ModuleSkeleton />
      </ModulePage>
    )
  }
  return <Loaded projectId={projectId} />
}

export { ProjectOverviewPage as ProjectOverview }

function Loaded({ projectId }: { projectId: string }) {
  const project = useProject(projectId)
  const resources = useProjectResources(projectId)
  const tasks = useProjectDevelopmentTasks(projectId)
  const recent = useProjectTimeline(projectId)
  const imported = useProjectImportByProject(projectId)
  const releaseSummary = useProjectStaticSiteSummary(projectId)
  const envs = useProjectEnvironments(projectId)
  const live = useLiveActivity()
  const latestByTask = useMemo(
    () => reduceLiveEvents(eventsForProject(live.events, projectId)).latestByTask,
    [live.events, projectId],
  )

  if (project.isPending) {
    return (
      <ModulePage testid="project-module-overview">
        <ModuleSkeleton />
      </ModulePage>
    )
  }
  if (project.error) {
    return (
      <ModulePage testid="project-module-overview">
        <ModuleError message={project.error.message} />
      </ModulePage>
    )
  }
  if (!project.data) {
    return (
      <ModulePage testid="project-module-overview">
        <ModuleSkeleton />
      </ModulePage>
    )
  }

  const activeTasks = (tasks.data ?? []).filter((task) => {
    const state = latestByTask.get(task.id)?.state ?? task.state
    return isExecutingDevelopmentTask(state) && !task.stale
  })
  const issueHosts = (resources.data?.hosts ?? []).filter(
    (host) =>
      (host.occupancy === 'exclusive' || host.occupancy === 'shared') &&
      (host.status === 'offline' || host.status === 'error'),
  )
  const importStatus = imported.error instanceof ApiFailure ? imported.error.status : undefined
  const decideCount = (tasks.data ?? []).filter((task) => {
    const state = latestByTask.get(task.id)?.state ?? task.state
    return developmentTaskNeedsDecision(state, task.stale)
  }).length

  return (
    <ModulePage testid="project-module-overview">
      <ModuleHeader
        title={project.data.name}
        subtitle={<PJ id="pjshell.overview.subtitle" />}
        actions={
          <>
            <Link
              to="/projects/$projectId/conversations"
              params={{ projectId }}
              className={luluButtonClass}
            >
              <T id="chat.list.account_group" />
            </Link>
            <Link to="/projects/$projectId/releases" params={{ projectId }} className={luluPrimaryButtonClass}>
              <T id="nav.ptab.release" />
              <span className="pj-btn-aff" aria-hidden="true">
                <Rocket size={12} strokeWidth={1.6} />
              </span>
            </Link>
          </>
        }
      />

      {!tasks.isPending && !tasks.error && decideCount > 0 ? (
        <ModuleNotice
          tone="warn"
          title={<PJ id="pjshell.overview.decide" vars={{ count: String(decideCount) }} />}
          body={<PJ id="pjshell.overview.decide.body" />}
          action={
            <Link to="/projects/$projectId/tasks" params={{ projectId }} className={luluPrimaryButtonClass}>
              <PJ id="pjshell.overview.decide.action" />
            </Link>
          }
          testid="pjshell-decide"
        />
      ) : null}

      <div
        data-testid="pjshell-overview-cards"
        className="mb-7 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
      >
        <LivesCard projectId={projectId} resources={resources} />
        <EntryCard projectId={projectId} resources={resources} />
        <PublishCard projectId={projectId} releaseSummary={releaseSummary} envs={envs} />
      </div>

      <GroupLabel>
        <PJ id="pjshell.overview.now" />
      </GroupLabel>
      <div className="grid grid-cols-1 gap-npc-gap-sm lg:grid-cols-2">
        <ModulePanel title={<PJ id="pjshell.overview.health" />} testid="pjshell-health">
          {project.data.health_text ? (
            <span data-testid="pjshell-health-text">{project.data.health_text}</span>
          ) : (
            <Unverified testid="pjshell-health-unverified" />
          )}
        </ModulePanel>

        <ModulePanel title={<PJ id="pjshell.overview.working" />} emptyish testid="pjshell-tasks">
          {tasks.isPending ? (
            <ModuleSkeleton />
          ) : tasks.error ? (
            <Unverified />
          ) : activeTasks.length === 0 ? (
            <PJ id="pjshell.overview.working.empty" />
          ) : (
            <ul className="m-0 flex list-none flex-col gap-npc-gap-xs p-0">
              {activeTasks.map((task) => {
                const elapsedMs = visibleTaskElapsedMs(task)
                const elapsed =
                  elapsedMs != null ? duration(Math.max(0, Math.floor(elapsedMs / 1000))) : null
                const who =
                  task.current_attempt?.executor_model?.trim() || task.developer_model?.trim() || null
                return (
                  <li key={task.id} className="flex min-w-0 flex-col gap-npc-gap-xs">
                    <span>{task.title}</span>
                    <span className="text-npc-meta text-npc-lulu-t3">
                      {who ?? <Unverified />}
                      {elapsed ? (
                        <>
                          {' · '}
                          <PJ id="pjshell.task.elapsed" vars={{ elapsed }} />
                        </>
                      ) : null}
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
          <p className="mb-0 mt-npc-gap-sm text-npc-meta text-npc-lulu-t4">
            <PJ id="pjshell.overview.working.note" />
          </p>
          <GoLink to="/projects/$projectId/tasks" projectId={projectId}>
            <PJ id="pjshell.overview.go_tasks" />
          </GoLink>
        </ModulePanel>

        <ModulePanel title={<PJ id="pjshell.overview.live_version" />} emptyish testid="pjshell-env">
          {envs.isPending ? (
            <ModuleSkeleton />
          ) : (
            <KvRows
              rows={[
                {
                  key: <PJ id="pjshell.env.production" />,
                  value: (
                    <span>
                      {project.data.public_url?.trim() ? (
                        <span className="font-npc-mono">{project.data.public_url}</span>
                      ) : (
                        <Unverified />
                      )}
                      {' · '}
                      {envs.error ? (
                        <Unverified />
                      ) : (
                        <EnvStatusLine env={environmentOf(envs.data, 'production')} />
                      )}
                    </span>
                  ),
                },
                {
                  key: <PJ id="pjshell.env.probe" />,
                  value: (
                    <ProbeValue
                      at={resources.data?.latest_probe_at}
                      pending={resources.isPending}
                      error={Boolean(resources.error)}
                    />
                  ),
                },
                {
                  key: <PJ id="pjshell.env.test" />,
                  value: envs.error ? <Unverified /> : <EnvStatusLine env={environmentOf(envs.data, 'test')} />,
                },
                {
                  key: <PJ id="pjshell.env.probe" />,
                  value: (
                    <ProbeValue
                      at={resources.data?.latest_probe_at}
                      pending={resources.isPending}
                      error={Boolean(resources.error)}
                    />
                  ),
                },
              ]}
            />
          )}
          <p className="mb-0 mt-npc-gap-sm text-npc-meta text-npc-lulu-t4">
            <PJ id="pjshell.overview.live.note" />
          </p>
          <GoLink to="/projects/$projectId/environments" projectId={projectId}>
            <PJ id="pjshell.overview.go_env" />
          </GoLink>
        </ModulePanel>

        <ModulePanel title={<PJ id="pjshell.overview.last_release" />} emptyish testid="pjshell-release">
          <ReleaseFacts summary={releaseSummary.data} pending={releaseSummary.isPending} error={Boolean(releaseSummary.error)} />
          <GoLink to="/projects/$projectId/releases" projectId={projectId}>
            <PJ id="pjshell.overview.go_release" />
          </GoLink>
        </ModulePanel>
      </div>

      <div className="mt-npc-gap-sm grid grid-cols-1 gap-npc-gap-sm lg:grid-cols-2">
        <ModulePanel title={<PJ id="pjshell.overview.capacity" />} testid="pjshell-capacity">
          <CapacityFacts resources={resources.data} pending={resources.isPending} error={Boolean(resources.error)} />
          <GoLink to="/projects/$projectId/resources" projectId={projectId}>
            <PJ id="pjshell.overview.go_resources" />
          </GoLink>
        </ModulePanel>
      </div>

      <GroupLabel>
        <PJ id="pjshell.overview.watch" />
      </GroupLabel>
      <div className="grid grid-cols-1 gap-npc-gap-sm lg:grid-cols-2">
        <ModulePanel title={<PJ id="pjshell.overview.resource_issues" />} testid="pjshell-resource-issues">
          {resources.isPending ? (
            <ModuleSkeleton />
          ) : resources.error ? (
            <Unverified />
          ) : (resources.data?.hosts.length ?? 0) === 0 ? (
            <div className="text-npc-lulu-t2">
              <PJ id="pjshell.resource.empty.title" />
            </div>
          ) : issueHosts.length === 0 ? (
            <PJ id="pjshell.overview.no_resource_issue" />
          ) : (
            <ul className="m-0 flex list-none flex-col gap-npc-gap-xs p-0">
              {issueHosts.map((host) => (
                <li key={host.id}>
                  {host.name}
                  {host.probe_status ? ` · ${host.probe_status}` : null}
                </li>
              ))}
            </ul>
          )}
          <GoLink to="/projects/$projectId/resources" projectId={projectId}>
            <PJ id="pjshell.overview.go_resources" />
          </GoLink>
        </ModulePanel>

        <ModulePanel title={<PJ id="pjshell.overview.takeover" />} testid="pjshell-takeover">
          {imported.isPending ? (
            <ModuleSkeleton />
          ) : importStatus === 404 ? (
            <PJ id="pjshell.takeover.none" />
          ) : imported.error ? (
            <T id="project_import.card.unavailable" />
          ) : (
            <ProjectImportCard projectId={projectId} surface="lulu" />
          )}
        </ModulePanel>
      </div>

      <div className="mt-npc-gap-sm">
        <ModulePanel title={<PJ id="pjshell.overview.feed" />} testid="pjshell-events">
          <p className="mb-npc-gap-sm mt-0 text-npc-meta text-npc-lulu-t4">
            <PJ id="pjshell.overview.feed.note" />
          </p>
          {recent.isPending ? (
            <ModuleSkeleton />
          ) : recent.error ? (
            <ModuleError message={recent.error.message} />
          ) : (recent.data?.items.length ?? 0) === 0 ? (
            <T id="project.feed.empty" />
          ) : (
            <ul className="m-0 flex list-none flex-col gap-npc-gap-xs p-0">
              {(recent.data?.items ?? []).map((item) => (
                <li key={item.id} className="flex justify-between gap-npc-gap-sm">
                  <span className="min-w-0 truncate">{item.title}</span>
                  <time dateTime={item.at} className="shrink-0 font-npc-mono text-npc-meta text-npc-lulu-t3">
                    {hhmm(item.at)}
                  </time>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-npc-gap-sm">
            <Link
              to="/projects/$projectId/activity"
              params={{ projectId }}
              className="text-npc-caption text-npc-lulu-t3 hover:text-npc-lulu-t1"
            >
              <T id="activity.overview.see_all" />
            </Link>
          </div>
        </ModulePanel>
      </div>

      <div className="mt-npc-gap-sm">
        <ModuleEvolutionSection projectId={projectId} />
      </div>
    </ModulePage>
  )
}

function GoLink({
  to,
  projectId,
  children,
}: {
  to: '/projects/$projectId/tasks' | '/projects/$projectId/environments' | '/projects/$projectId/releases' | '/projects/$projectId/resources'
  projectId: string
  children: ReactNode
}) {
  return (
    <div className="mt-npc-gap-sm">
      <Link to={to} params={{ projectId }} className="pj-link text-npc-caption">
        {children}
      </Link>
    </div>
  )
}

function EnvStatusLine({ env }: { env: Schemas['ProjectEnvironment'] | null }) {
  const copyId = environmentStatusCopy(env)
  if (copyId === 'unverified') return <Unverified />
  return <PJ id={copyId} />
}

function ProbeValue({
  at,
  pending,
  error,
}: {
  at: string | null | undefined
  pending: boolean
  error: boolean
}) {
  if (pending) return <ModuleSkeleton />
  if (error) return <Unverified />
  if (!at) return <Uncollected />
  return <span className="font-npc-mono">{monthDayTime(at)}</span>
}

function CapacityFacts({
  resources,
  pending,
  error,
}: {
  resources: Schemas['ProjectResources'] | undefined
  pending: boolean
  error: boolean
}) {
  if (pending) return <ModuleSkeleton />
  if (error || !resources) return <Unverified />
  return (
    <KvRows
      rows={[
        {
          key: <PJ id="pjshell.capacity.host" />,
          value: (
            <span data-testid="pjshell-capacity-hosts">
              <T id="dashboard.project.host_count" vars={{ count: resources.assigned_count }} />
            </span>
          ),
        },
        {
          key: <PJ id="pjshell.capacity.cpu" />,
          value: resources.cpu_cores != null ? String(resources.cpu_cores) : <Uncollected />,
        },
        {
          key: <PJ id="pjshell.capacity.memory" />,
          value: resources.memory_bytes != null ? bytes(resources.memory_bytes) : <Uncollected />,
        },
        {
          key: <PJ id="pjshell.capacity.disk" />,
          value:
            resources.disk_used_ratio != null ? (
              <T id={diskCapacityId(resources.disk_used_ratio)} />
            ) : resources.latest_probe_at ? (
              <PJ id="pjshell.fact.probe_failed" />
            ) : resources.disk_bytes != null ? (
              bytes(resources.disk_bytes)
            ) : (
              <Uncollected />
            ),
        },
        {
          key: <PJ id="pjshell.capacity.expiry" />,
          value: resources.earliest_expiry_at ? monthDayTime(resources.earliest_expiry_at) : <Uncollected />,
        },
      ]}
    />
  )
}

function ReleaseFacts({
  summary,
  pending,
  error,
}: {
  summary: Schemas['ProjectStaticSiteSummary'] | undefined
  pending: boolean
  error: boolean
}) {
  if (pending) return <ModuleSkeleton />
  if (error || !summary) return <Unverified />
  if (summary.site_count === 0 && summary.live_version_no == null) return <PJ id="pjshell.release.none" />
  const live = summary.live_version_no
  const who = summary.live_created_by_display?.trim()
  const durationMs = summary.live_duration_ms
  return (
    <KvRows
      rows={[
        {
          key: <PJ id="pjshell.release.version" />,
          value:
            live != null ? (
              <span className="font-npc-mono">{`v${live}`}</span>
            ) : (
              <Unverified />
            ),
        },
        { key: <PJ id="pjshell.release.what" />, value: <Unverified /> },
        { key: <PJ id="pjshell.release.who" />, value: who ? who : <Unverified /> },
        {
          key: <PJ id="pjshell.release.duration" />,
          value:
            durationMs != null ? (
              duration(Math.max(0, Math.floor(durationMs / 1000)))
            ) : (
              <Unverified />
            ),
        },
        { key: <PJ id="pjshell.release.previous" />, value: <Unverified /> },
      ]}
    />
  )
}

function LivesCard({
  projectId,
  resources,
}: {
  projectId: string
  resources: ReturnType<typeof useProjectResources>
}) {
  const hosts = (resources.data?.hosts ?? []).filter(
    (host) => host.occupancy === 'exclusive' || host.occupancy === 'shared',
  )
  const shown = hosts.slice(0, 3)
  return (
    <ModulePanel title={<PJ id="pjshell.overview.card.lives" />} emptyish testid="pjshell-card-lives">
      {resources.isPending ? (
        <ModuleSkeleton />
      ) : resources.error ? (
        <Unverified />
      ) : hosts.length === 0 ? (
        <PJ id="pjshell.resource.empty.title" />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-npc-gap-xs p-0">
          {shown.map((host) => (
            <li key={host.id} className="flex min-w-0 flex-col gap-npc-gap-xs">
              <Link to="/hosts/$hostId" params={{ hostId: host.id }} className="text-npc-lulu-t1 hover:underline">
                {host.name}
              </Link>
              <span className="text-npc-meta text-npc-lulu-t3">
                {host.occupancy === 'exclusive' ? (
                  <PJ id="pjshell.resource.exclusive" />
                ) : host.occupancy === 'shared' ? (
                  <PJ id="pjshell.resource.shared" />
                ) : (
                  <Uncollected />
                )}
                {' · '}
                {host.environment_kind === 'production' ? (
                  <PJ id="pjshell.env.production" />
                ) : host.environment_kind === 'test' ? (
                  <PJ id="pjshell.env.test" />
                ) : (
                  <Uncollected />
                )}
                {' · '}
                {diskCapacityKind(host) === 'failed' ? (
                  <PJ id="pjshell.fact.probe_failed" />
                ) : host.disk_used_ratio != null ? (
                  <T id={diskCapacityId(host.disk_used_ratio)} />
                ) : (
                  <Uncollected />
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      <GoLink to="/projects/$projectId/resources" projectId={projectId}>
        <PJ id="pjshell.overview.go_resources" />
      </GoLink>
    </ModulePanel>
  )
}

function EntryCard({
  projectId,
  resources,
}: {
  projectId: string
  resources: ReturnType<typeof useProjectResources>
}) {
  const name = resources.data?.dns_name?.trim()
  const configured = Boolean(name) && resources.data?.dns_status === 'configured'
  return (
    <ModulePanel title={<PJ id="pjshell.overview.card.entry" />} emptyish testid="pjshell-card-entry">
      {resources.isPending ? (
        <ModuleSkeleton />
      ) : resources.error ? (
        <Unverified />
      ) : configured && name ? (
        <div className="flex min-w-0 flex-col gap-npc-gap-xs">
          <span className="font-npc-mono">{name}</span>
          {resources.data?.latest_probe_at && receiptOpenHref(name) ? (
            <a href={receiptOpenHref(name)!} className="text-npc-caption text-npc-lulu-t3 hover:text-npc-lulu-t1">
              <T id="project.action.open_public_entry" />
            </a>
          ) : null}
        </div>
      ) : (
        <PJ id="pjshell.fact.unconfigured" />
      )}
      <p className="mb-0 mt-npc-gap-sm text-npc-meta text-npc-lulu-t4">
        <PJ id="pjshell.overview.live.note" />
      </p>
      <GoLink to="/projects/$projectId/environments" projectId={projectId}>
        <PJ id="pjshell.overview.go_env" />
      </GoLink>
    </ModulePanel>
  )
}

function PublishCard({
  projectId,
  releaseSummary,
  envs,
}: {
  projectId: string
  releaseSummary: ReturnType<typeof useProjectStaticSiteSummary>
  envs: ReturnType<typeof useProjectEnvironments>
}) {
  const summary = releaseSummary.data
  const hasStatic = Boolean(summary && (summary.site_count > 0 || summary.live_version_no != null))
  const hasApp = (envs.data?.items ?? []).some((env) => Boolean(env.active_release))
  return (
    <ModulePanel title={<PJ id="pjshell.overview.card.publish" />} emptyish testid="pjshell-card-publish">
      {releaseSummary.isPending ? (
        <ModuleSkeleton />
      ) : releaseSummary.error ? (
        <Unverified />
      ) : hasStatic ? (
        <div className="flex min-w-0 flex-col gap-npc-gap-xs">
          <PJ id="pjshell.release.static" />
          {summary?.live_version_no != null ? (
            <span className="font-npc-mono">{`v${summary.live_version_no}`}</span>
          ) : (
            <Unverified />
          )}
        </div>
      ) : hasApp ? (
        <PJ id="pjshell.release.app" />
      ) : (
        <PJ id="pjshell.release.none" />
      )}
      <GoLink to="/projects/$projectId/releases" projectId={projectId}>
        <PJ id="pjshell.overview.go_release" />
      </GoLink>
    </ModulePanel>
  )
}
